import { theme } from 'antd';
import {
	CategoryIcon,
	CreditCardIcon,
	DeviceGroupsIcon,
	EnableIcon,
	InfoIcon,
	StatusIcon,
	VideoFeedIcon
} from 'assets/images/svg';
import { constants } from 'config/constants';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectDevicesByDeviceID, selectDeviceFieldsByDeviceID } from 'reduxStore/devices/selectors';
import { Loader } from 'zComponents/atoms';
import { ManageDeviceDropdown } from 'zComponents/molecules';
import { Button, Col, Divider, Drawer, Flex, Row } from 'zui/Antd';
import { InfoTuple } from 'zui/Custom';
import {
	AccessPermissionsPanelContent,
	AccessPermissionsPanelExtra,
	AdditionalInformationPanelContent,
	AdditionalInformationPanelExtra,
	CredentialPanelContent,
	CredentialPanelExtra,
	DevicesGroupPanelContent,
	DevicesGroupPanelExtra,
	EditDrawerHeader,
	EditSiteSelect,
	PanelHeader
} from './components';
import { selectCategories } from 'reduxStore/categories/selectors';
import Collapse from 'zui/Antd/Collapse';
import { SitesIcon } from 'assets/images/svg/Menu';

const DeviceDrawer = ({ deviceID, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hooks
	const {
		token: { borderRadiusLG, paddingXXL = 48, paddingMD, paddingLG, marginXS, paddingSM, padding }
	} = theme.useToken();
	const devicesRef = useRef(null);
	const permissionRef = useRef(null);
	const additionalInfoRef = useRef(null);
	const credentialsRef = useRef(null);
	const navigate = useNavigate();

	// ✅ Redux State
	const device = useSelector((state) => selectDevicesByDeviceID(state, deviceID));
	const deviceFields = useSelector((state) => selectDeviceFieldsByDeviceID(state, deviceID));
	const categories = useSelector(selectCategories);

	// ✅ Local State
	const [internalVisible, setInternalVisible] = useState(false);
	const [activeKeys, setActiveKeys] = useState([
		'permissions',
		'credentials',
		'accessCards',
		'deviceGroups',
		'additionalInformation'
	]);
	const [isEditingDevices, setIsEditingDevices] = useState(false);
	const [isEditingPermissions, setIsEditingPermissions] = useState(false);
	const [isEditingAdditionalInfo, setIsEditingAdditionalInfo] = useState(false);
	const [isEditingCredentials, setIsEditingCredentials] = useState(false);
	const [isVideoHovered, setIsVideoHovered] = useState(null);
	const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
	const [isDeviceGroupsModalOpen, setIsDeviceGroupsModalOpen] = useState(false);
	const [isAdditionalInfoModalOpen, setIsAdditionalInfoModalOpen] = useState(false);
	const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);

	const visible = visibleProp ? visibleProp : internalVisible;
	const setVisible = setVisibleProp ? setVisibleProp : setInternalVisible;

	// ✅ Close drawer and reset everything
	const handleOnClose = () => {
		setVisible(false);
	};

	const togglePanel = (key) => {
		setActiveKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	};

	const handleCollapseChange = useCallback(
		(keys) => {
			if (
				isPermissionsModalOpen ||
				isDeviceGroupsModalOpen ||
				isAdditionalInfoModalOpen ||
				isCredentialsModalOpen
			) {
				return;
			}

			setActiveKeys(keys);

			if (!keys.includes('deviceGroups') && isEditingDevices) {
				setIsEditingDevices(false);
			}
			if (!keys.includes('permissions') && isEditingPermissions) {
				setIsEditingPermissions(false);
			}
			if (!keys.includes('additionalInformation') && isEditingAdditionalInfo) {
				setIsEditingAdditionalInfo(false);
			}
			if (!keys.includes('credentials') && isEditingCredentials) {
				setIsEditingCredentials(false);
			}
		},
		[
			isEditingAdditionalInfo,
			isEditingDevices,
			isPermissionsModalOpen,
			isAdditionalInfoModalOpen,
			isDeviceGroupsModalOpen,
			isEditingPermissions,
			isEditingCredentials,
			isCredentialsModalOpen
		]
	);
	const items = useMemo(
		() => [
			{
				key: 'permissions',
				label: (
					<PanelHeader
						title='Access Permissions'
						iconLeft={<EnableIcon width={16} height={16} />}
						isExpanded={activeKeys.includes('permissions')}
						togglePanel={() => togglePanel('permissions')}
					/>
				),
				children: (
					<div ref={permissionRef}>
						<AccessPermissionsPanelContent deviceID={deviceID} />
					</div>
				),
				extra: (
					<AccessPermissionsPanelExtra
						deviceID={deviceID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsPermissionsModalOpen}
					/>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			},
			{
				key: 'divider-3',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'credentials',
				label: (
					<PanelHeader
						title='Credentials'
						iconLeft={<CreditCardIcon />}
						isExpanded={activeKeys.includes('credentials')}
						togglePanel={() => togglePanel('credentials')}
					/>
				),
				children: (
					<div ref={credentialsRef}>
						<CredentialPanelContent deviceID={deviceID} />
					</div>
				),
				extra: (
					<CredentialPanelExtra
						deviceID={deviceID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsCredentialsModalOpen}
					/>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			},
			{
				key: 'divider-2',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'deviceGroups',
				label: (
					<PanelHeader
						title='Device Groups'
						iconLeft={<DeviceGroupsIcon width={16} height={16} />}
						isExpanded={activeKeys.includes('deviceGroups')}
						togglePanel={() => togglePanel('deviceGroups')}
					/>
				),
				children: (
					<div ref={devicesRef}>
						<DevicesGroupPanelContent deviceID={deviceID} />
					</div>
				),
				extra: (
					<DevicesGroupPanelExtra
						deviceID={deviceID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsDeviceGroupsModalOpen}
					/>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			},
			{
				key: 'divider-3',
				label: <Divider style={{ margin: 0 }} />,
				children: null,
				collapsible: 'disabled',
				showArrow: false,
				style: { padding: 0 }
			},
			{
				key: 'additionalInformation',
				label: (
					<PanelHeader
						title='Additional Information'
						iconLeft={<InfoIcon />}
						isExpanded={activeKeys.includes('additionalInformation')}
						togglePanel={() => togglePanel('additionalInformation')}
					/>
				),
				children: (
					<div ref={additionalInfoRef}>
						<AdditionalInformationPanelContent deviceID={deviceID} />
					</div>
				),
				extra: (
					<AdditionalInformationPanelExtra
						deviceID={deviceID}
						activeKeys={activeKeys}
						setActiveKeys={setActiveKeys}
						onModalOpenChange={setIsAdditionalInfoModalOpen}
					/>
				),
				styles: {
					header: {
						marginTop: marginXS
					}
				}
			}
		],
		[activeKeys, setActiveKeys, deviceID, marginXS]
	);

	const deviceCategory = useMemo(() => {
		return categories.find((category) => category.categoryID === device?.categoryID)?.name || 'Unknown';
	}, [categories, device?.categoryID]);

	const videoLinkField = useMemo(() => {
		if (!deviceFields) return null;

		const field = deviceFields.find(
			(field) => field.fieldTypeID === constants.VIDEO_FIELD_TYPE_ID && field.value && field.value.trim() !== ''
		);

		// Validate if the URL is actually a valid URL
		if (field) {
			try {
				new URL(field.value);
				return field;
			} catch (error) {
				console.warn('Invalid video URL provided:', field.value);
				return null;
			}
		}

		return null;
	}, [deviceFields]);

	// Memoize drawer styles
	const drawerStyles = useMemo(
		() => ({
			wrapper: { margin: '30px 20px 30px 0px' },
			content: { borderRadius: borderRadiusLG },
			body: { padding: paddingXXL }
		}),
		[borderRadiusLG, paddingXXL]
	);
	return (
		<Drawer
			placement='right'
			onClose={handleOnClose}
			open={visible}
			width={871}
			closeIcon={false}
			styles={drawerStyles}
			destroyOnClose={true}
		>
			{!device ? (
				<Loader />
			) : (
				<Flex vertical>
					{/* Header */}
					<EditDrawerHeader
						deviceID={device?.deviceID}
						defaultCategoryID={device?.defaultCategoryID}
						name={device?.name}
						description={device?.deviceID}
						handleOnCloseEdit={handleOnClose}
					/>
					{/* Manage Dropdown */}
					<Flex
						style={{
							marginTop: paddingXXL
						}}
						justify='flex-start'
					>
						<ManageDeviceDropdown deviceID={deviceID} />
						<Button
							style={{ marginLeft: paddingSM }}
							disabled={!device?.location || device.location.length === 0}
							onClick={() => {
								if (device?.location && device.location.length > 0) {
									navigate(
										`/access/floorplan?tab=mapview&deviceID=${encodeURIComponent(device.deviceID)}`
									);
								}
							}}
						>
							View on Floorplan
						</Button>
					</Flex>

					<Divider style={{ margin: `${paddingMD}px 0px` }} />

					<Row>
						<Col span={24}>
							<Flex vertical gap={paddingLG} style={{ padding: padding }}>
								<InfoTuple icon={<CategoryIcon />} text='Category' value={deviceCategory} />

								<InfoTuple icon={<StatusIcon />} text='Status' value={'-'} highlight={true} />

								<InfoTuple
									icon={<SitesIcon />}
									text='Sites'
									align='start'
									value={
										<EditSiteSelect deviceID={deviceID} />
									}
								/>

								{/* Video Feed Section for Camera Devices */}
								{videoLinkField && (
									<>
										<InfoTuple
											icon={<VideoFeedIcon />}
											text='Video Feed'
											align='start'
											value={
												<Flex style={{ position: 'relative', height: 110 }}>
													<Flex
														style={{
															display: 'inline-block',
															cursor: 'pointer',
															zIndex: 9,
															position: 'absolute',
															right: 0,
															transition: 'all 0.3s ease-in-out',
															overflow: 'hidden',
															borderRadius: borderRadiusLG
														}}
														onMouseEnter={() => setIsVideoHovered(true)}
														onMouseLeave={() => setIsVideoHovered(false)}
													>
														<iframe
															src={videoLinkField.value}
															width={isVideoHovered ? '620' : '160'}
															height={isVideoHovered ? '420' : '110'}
															style={{
																border: 'none',
																borderRadius: borderRadiusLG,
																display: 'block',
																transition: 'all 0.3s ease-in-out'
															}}
															allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share'
															referrerpolicy='strict-origin-when-cross-origin'
															title={`Device Video Feed ${deviceID}`}
														/>
													</Flex>
												</Flex>
											}
										/>
									</>
								)}
							</Flex>
						</Col>
					</Row>

					<Divider style={{ margin: `${paddingMD}px 0px` }} />

					{/* Collapse Section (Device Groups) */}
					<Collapse
						expandIcon={() => null}
						items={items}
						style={{ margin: 0, padding: 0 }}
						activeKey={activeKeys}
						onChange={handleCollapseChange}
						ghost
					/>
				</Flex>
			)}
		</Drawer>
	);
};

export default DeviceDrawer;
